const MAX_EVENTS = 200;
const DEBUG_STORAGE_KEY = "bezoned.audio-context-debug";

type AudioContextMethod = "close" | "resume" | "suspend";

interface AudioContextEvent {
  at: string;
  contextId?: number;
  details?: Record<string, unknown>;
  state?: AudioContextState;
  type: AudioContextEventType | "marker";
}

type AudioContextEventType =
  | "close-error"
  | "close-requested"
  | "close-resolved"
  | "created"
  | "resume-error"
  | "resume-requested"
  | "resume-resolved"
  | "state-change"
  | "suspend-error"
  | "suspend-requested"
  | "suspend-resolved";

interface AudioContextRecord {
  createdAt: string;
  id: number;
  sampleRate: number;
  stack: string | undefined;
  state: AudioContextState;
}

const canUseWindow = typeof window !== "undefined";

if (canUseWindow && window.AudioContext && !window.__bezonedAudioContexts) {
  const OriginalAudioContext = window.AudioContext;
  const contexts = new Map<AudioContext, AudioContextRecord>();
  const events: AudioContextEvent[] = [];
  let isEnabled =
    import.meta.env.DEV ||
    import.meta.env.PUBLIC_ENVIRONMENT === "staging" ||
    new URLSearchParams(window.location.search).has("audioContextDebug") ||
    window.localStorage.getItem(DEBUG_STORAGE_KEY) === "true";
  let nextContextId = 1;

  const appendEvent = (event: AudioContextEvent) => {
    events.push(event);
    if (events.length > MAX_EVENTS) {
      events.shift();
    }

    if (isEnabled) {
      console.info("[AudioContext]", event);
    }
  };

  const record = (context: AudioContext, type: AudioContextEventType) => {
    const trackedContext = contexts.get(context);
    if (!trackedContext) {
      return;
    }

    trackedContext.state = context.state;
    appendEvent({
      at: new Date().toISOString(),
      contextId: trackedContext.id,
      state: context.state,
      type,
    });

    if (isEnabled) {
      console.info("[AudioContext] context", trackedContext);
    }
  };

  const trackMethod = (context: AudioContext, method: AudioContextMethod) => {
    const originalMethod = context[method].bind(context);
    Object.defineProperty(context, method, {
      configurable: true,
      value: async () => {
        record(context, `${method}-requested`);
        try {
          const result = await originalMethod();
          record(context, `${method}-resolved`);
          if (method === "close") {
            contexts.delete(context);
          }
          return result;
        } catch (error) {
          record(context, `${method}-error`);
          throw error;
        }
      },
      writable: true,
    });
  };

  class TrackedAudioContext extends OriginalAudioContext {
    constructor(...args: ConstructorParameters<typeof AudioContext>) {
      super(...args);

      contexts.set(this, {
        createdAt: new Date().toISOString(),
        id: nextContextId++,
        sampleRate: this.sampleRate,
        stack: new Error("AudioContext created").stack,
        state: this.state,
      });
      this.addEventListener("statechange", () => record(this, "state-change"));
      trackMethod(this, "close");
      trackMethod(this, "resume");
      trackMethod(this, "suspend");
      record(this, "created");
    }
  }

  window.AudioContext = TrackedAudioContext;
  window.__bezonedAudioContexts = {
    contexts: () => [...contexts.values()],
    disable: () => {
      isEnabled = false;
      window.localStorage.removeItem(DEBUG_STORAGE_KEY);
    },
    enable: () => {
      isEnabled = true;
      window.localStorage.setItem(DEBUG_STORAGE_KEY, "true");
    },
    events: () => [...events],
    isEnabled: () => isEnabled,
    mark: (label, details = {}) => {
      appendEvent({
        at: new Date().toISOString(),
        details: { label, ...details },
        type: "marker",
      });
    },
  };

  document.addEventListener("visibilitychange", () => {
    appendEvent({
      at: new Date().toISOString(),
      details: { visibilityState: document.visibilityState },
      type: "marker",
    });
  });
}

export const markAudioContextDiagnostic = (
  label: string,
  details: Record<string, unknown> = {}
) => {
  if (!canUseWindow) {
    return;
  }

  const diagnostics = window.__bezonedAudioContexts;
  if (!diagnostics) {
    return;
  }

  diagnostics.mark(label, details);
};
